import React from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { LICENCE_KEY } from "../constants/charts";

am4core.addLicense(LICENCE_KEY);

function AmChartSimpleColumn({
  chartId,
  chartData,
  categoryKey,
  valueKey,
  color,
  width,
  height,
  positiveNegative,
  colorNegative = "#d9534f",
  onClick,
  additionalSeries,
  additionalSeriesColor = "#777",
  colorKey,
  whiteChart
}) {
  const holderRef = React.useRef(null);
  const chartRef = React.useRef(null);
  const mainSeriesRef = React.useRef(null);
  const extraSeriesRef = React.useRef(null);

  // ---- CREATE ONCE ----
  React.useEffect(() => {
    if (!holderRef.current) return;

    const chart = am4core.create(holderRef.current, am4charts.XYChart);
    chartRef.current = chart;

    chart.paddingRight = 20;
    chart.fontSize = 11;
    chart.strokeOpacity = 0;
    chart.numberFormatter.numberFormat = "#.00a";
    chart.numberFormatter.bigNumberPrefixes = [
      { number: 1e3, suffix: "K" },
      { number: 1e6, suffix: "M" },
      { number: 1e9, suffix: "Bn" }
    ];

    const textColor = whiteChart ? am4core.color("#fff") : am4core.color("#000");

    // Axes
    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = categoryKey;
    categoryAxis.renderer.minGridDistance = 15;
    categoryAxis.renderer.grid.template.strokeOpacity = 0;
    categoryAxis.renderer.labels.template.fontSize = 9;
    categoryAxis.renderer.labels.template.fill = textColor;
    categoryAxis.renderer.labels.template.rotation = -90;
    categoryAxis.renderer.labels.template.horizontalCenter = "right";
    categoryAxis.renderer.labels.template.location = 0;
    categoryAxis.renderer.axisFills.template.disabled = false;
    categoryAxis.renderer.axisFills.template.fillOpacity = 0.05;
    if (whiteChart) categoryAxis.renderer.axisFills.template.fill = am4core.color("#fff");
    categoryAxis.renderer.axisFills.template.strokeOpacity = 0;

    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.renderer.labels.template.fill = textColor;
    valueAxis.renderer.labels.template.fontSize = 9;
    valueAxis.strokeOpacity = 0;

    // Main series
    const series = chart.series.push(new am4charts.ColumnSeries());
    mainSeriesRef.current = series;
    series.dataFields.categoryX = categoryKey;
    series.dataFields.valueY = valueKey;
    series.columns.template.strokeWidth = 0;
    series.columns.template.fillOpacity = 0.9;
    series.columns.template.tooltipText = `{${categoryKey}}: [bold]{${valueKey}}[/]`;

    // make sure a cursor exists
    chart.cursor = new am4charts.XYCursor();

    series.sequencedInterpolation = true;
    series.interpolationDuration = 600;
    series.interpolationEasing = am4core.ease.quadOut;

    // Hover state
    const hs = series.columns.template.states.create("hover", {});
    hs.properties.scale = 1.02;

    if (whiteChart) {
      series.tooltip.getFillFromObject = false;
      series.tooltip.background.fill = am4core.color("#fff");
      series.tooltip.label.fill = am4core.color("#000");
    }

    series.columns.template.events.on("hit", ev => {
      if (!onClick) return;
      const idx = ev?.target?.dataItem?.index;
      if (idx != null) onClick(idx);
    });

    // Optional additional series
    if (additionalSeries) {
      const s2 = chart.series.push(new am4charts.ColumnSeries());
      extraSeriesRef.current = s2;
      s2.dataFields.categoryX = categoryKey;
      s2.dataFields.valueY = additionalSeries;
      s2.columns.template.strokeWidth = 0;
      s2.columns.template.width = am4core.percent(50);
      s2.columns.template.adapter.add("fill", () => am4core.color(additionalSeriesColor));
      if (whiteChart) {
        s2.tooltip.getFillFromObject = false;
        s2.tooltip.background.fill = am4core.color("#fff");
        s2.tooltip.label.fill = am4core.color("#000");
      }
      s2.tooltipText = `{${categoryKey}}: {${additionalSeries}}`;
    }

    series.appear(600, 60);
    chart.appear(600, 0);

    return () => {
      chart.dispose();
      chartRef.current = null;
      mainSeriesRef.current = null;
      extraSeriesRef.current = null;
    };
  }, []);

  // ---- UPDATE DATA & AXIS ----
  React.useEffect(() => {
    const chart = chartRef.current;
    const series = mainSeriesRef.current;
    if (!chart || !series) return;

    const enriched = (chartData || []).map(row => {
      let fill = color || undefined;

      if (colorKey && row[colorKey]) {
        fill = row[colorKey];
      }

      if (positiveNegative) {
        const val = Number(row[valueKey] ?? 0);
        if (val < 0) fill = colorNegative;
        else if (!fill) fill = "#2a9d8f";
      }

      return { ...row, __fill: fill };
    });

    if (!series.columns.template.__hasFillAdapter) {
      series.columns.template.adapter.add("fill", (currentFill, target) => {
        const ctx = target?.dataItem?.dataContext;
        return ctx && ctx.__fill ? am4core.color(ctx.__fill) : currentFill;
      });
      series.columns.template.__hasFillAdapter = true;
    }

    chart.data = enriched;

    // --- SMART Y MAX ---
    const valueAxis = chart.yAxes.getIndex(0);
    if (valueAxis) {
      let maxY = 0;
      enriched.forEach(r => {
        const val = Number(r[valueKey] ?? 0);
        if (val > maxY) maxY = val;
      });

      const niceMax = Math.ceil(maxY / 10) * 10 || 10;
      valueAxis.min = 0;
      valueAxis.max = niceMax;
      valueAxis.strictMinMax = true;
    }

    chart.invalidateRawData();
  }, [chartData, color, colorKey, positiveNegative, colorNegative, valueKey]);

  // ---- UPDATE EXTRA SERIES ----
  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !extraSeriesRef.current) return;
    chart.invalidateRawData();
  }, [chartData, additionalSeries]);

  return (
    <div
      id={chartId}
      ref={holderRef}
      style={{ width: width || "100%", height: height || 280 }}
    />
  );
}

export default React.memo(AmChartSimpleColumn, (prev, next) =>
  prev.chartId === next.chartId &&
  prev.chartData === next.chartData &&
  prev.categoryKey === next.categoryKey &&
  prev.valueKey === next.valueKey &&
  prev.color === next.color &&
  prev.colorKey === next.colorKey &&
  prev.width === next.width &&
  prev.height === next.height &&
  prev.positiveNegative === next.positiveNegative &&
  prev.colorNegative === next.colorNegative &&
  prev.additionalSeries === next.additionalSeries &&
  prev.additionalSeriesColor === next.additionalSeriesColor &&
  prev.whiteChart === next.whiteChart
);



